import type { AxiosInstance } from 'axios'
import { RouterProvider } from 'react-router/dom'
import type { Store } from 'redux'
import { createBrowserRouter } from 'react-router'
import { useEffect } from 'react'

import { ApiProvider } from './components/ApiProvider'
import { Root } from './root'
import { generateRoutes } from './routes'
import { hydrateNavigation } from './store/actions'
import I18nProvider from './components/I18nProvider'

type Props = {
  store: Store & { api: AxiosInstance }
  additionalScripts: Array<string>
}

export default function App({ store, additionalScripts }: Readonly<Props>) {
  useEffect(() => {
    // Initialize additional scripts, mainly third-party scripts of storefront apps.
    // These scripts are preloaded with HTML `<link rel="preload">` elements via SSR.
    //
    // Similar to the window "load" event and `document.readyState`, we use a custom "app:load"
    // event and "readyState" to track the readiness of the app. This is used in the tracking
    // module to defer tracking until all scripts are loaded. We can’t use `document.readyState`
    // because these additional scripts are loaded dynamically after the window "load" event.
    // Only if all script loads are settled the app’s ready state is considered "complete".
    Promise.allSettled(additionalScripts.map((src) => loadScript(src))).then(() => {
      window.__EP.readyState = 'complete'
      document.body.dispatchEvent(new CustomEvent('app:load'))
    })

    store.dispatch(hydrateNavigation())
  }, [additionalScripts, store])

  const router = createBrowserRouter(generateRoutes(store, store.api), {
    hydrationData: window.__staticRouterHydrationData,
  })

  return (
    <ApiProvider api={store.api}>
      <Root store={store}>
        <I18nProvider>
          <RouterProvider router={router} />
        </I18nProvider>
      </Root>
    </ApiProvider>
  )
}

async function loadScript(src: string) {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.onload = () => resolve()
    script.onerror = reject
    document.head.appendChild(script)
  })
}
